import { Component, computed, effect, inject, input, OnDestroy, output, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TypographyComponent } from '@purolator/ui-kit-angular';
import type { CouponFieldStatus } from '../../../../core/api/coupon/models/coupon.model';
import type { CompletedShipmentPaymentDisplay } from '../../../../core/api/shipment/models/completed-shipment.model';
import {
  formatPaymentCardBrand,
  resolvePaymentCardBrandIconSrc,
} from '../../../../core/api/shipment/models/create-shipment.model';
import { TranslatePipe, TranslationService } from '../../../../core/i18n';
import type {
  EstimateBreakdownSurchargeLine,
  EstimateBreakdownView,
} from '../../../service-step/sections/delivery-service-selection/delivery-service-matrix';
import { formatCurrencyAmount } from '../../../service-step/sections/delivery-service-selection/delivery-service-matrix';
import type { SelectedDeliveryService } from '../../../service-step/sections/delivery-service-selection/delivery-service-selection.model';
import { formatReviewArrivalDate } from '../../utils/format-review-arrival-date';
import { ReviewCouponCode } from '../review-coupon-code/review-coupon-code';

/** Delay before showing the card overlay so fast coupon responses avoid a flash. */
const COUPON_LOADING_OVERLAY_DELAY_MS = 1000;

/**
 * Shipment Estimate Details block: selected service card, cost breakdown, and coupon or payment shell.
 */
@Component({
  selector: 'app-review-estimate-details',
  imports: [ReviewCouponCode, TranslatePipe, TypographyComponent],
  templateUrl: './review-estimate-details.html',
  styleUrl: './review-estimate-details.scss',
  host: {
    '[attr.aria-busy]': 'showCouponLoadingOverlay() ? "true" : null',
  },
})
export class ReviewEstimateDetails implements OnDestroy {
  private readonly translation = inject(TranslationService);

  readonly selectedService = input<SelectedDeliveryService | null>(null);
  readonly breakdown = input<EstimateBreakdownView | null>(null);
  /** Coupon control for Review & Pay; unused when showCoupon is false. */
  readonly couponControl = input<FormControl<string> | null>(null);
  readonly couponStatus = input<CouponFieldStatus>('idle');
  /** When false, hides the coupon column (Get Labels uses payment shell instead). */
  readonly showCoupon = input(true);
  /** i18n key for the section title (Review vs Get Labels copy). */
  readonly titleKey = input('createShipment.reviewAndPay.estimate.title');
  /** Payment Method / Billing Address shell for Get Labels Estimated Costs. */
  readonly paymentDisplay = input<CompletedShipmentPaymentDisplay | null>(null);
  readonly applied = output<string>();

  protected readonly showCouponLoadingOverlay = signal(false);
  private couponLoadingDelayTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly formatCurrencyAmount = formatCurrencyAmount;
  protected readonly formatPaymentCardBrand = formatPaymentCardBrand;
  protected readonly resolvePaymentCardBrandIconSrc = resolvePaymentCardBrandIconSrc;

  constructor() {
    effect(() => {
      const status = this.couponStatus();
      this.clearCouponLoadingDelayTimer();

      if (status === 'loading') {
        this.couponLoadingDelayTimer = setTimeout(() => {
          if (this.couponStatus() === 'loading') {
            this.showCouponLoadingOverlay.set(true);
          }
        }, COUPON_LOADING_OVERLAY_DELAY_MS);
        return;
      }

      this.showCouponLoadingOverlay.set(false);
    });
  }

  /** Clears the delayed overlay timer when coupon loading ends or the component is destroyed. */
  ngOnDestroy(): void {
    this.clearCouponLoadingDelayTimer();
  }

  protected readonly arrivalLabel = computed(() => {
    this.translation.revision();
    const deliveryDate = this.selectedService()?.deliveryDate;
    if (!deliveryDate) {
      return '';
    }

    const locale = this.translation.locale() === 'fr' ? 'fr' : 'en';
    const formatted = formatReviewArrivalDate(deliveryDate, locale);
    return this.translation.translate('createShipment.reviewAndPay.estimate.arriving', {
      date: formatted,
    });
  });

  /**
   * Resolves an i18n label for a surcharge/accessorial label code (same keys as Service step).
   */
  protected surchargeLabelKey(labelCode: string): string {
    const normalizedLabelCode = labelCode === 'SIG' ? 'RES' : labelCode;
    return `createShipment.serviceStep.deliveryService.breakdown.surcharges.${normalizedLabelCode}`;
  }

  /** Hides a redundant backend description when it repeats the visible surcharge label. */
  protected hasDistinctDescription(line: EstimateBreakdownSurchargeLine): boolean {
    const description = line.descriptionKey
      ? this.translation.translate(line.descriptionKey, line.descriptionParams).trim()
      : (line.description ?? '').trim();
    if (!description) {
      return false;
    }

    const label = this.translation.translate(this.surchargeLabelKey(line.labelCode)).trim();
    return description.localeCompare(label, undefined, { sensitivity: 'base' }) !== 0;
  }

  /**
   * Formats the backend coupon rate for the localized Pay only line.
   */
  protected discountParams(rate: number): Record<string, string> {
    return { rate: String(rate) };
  }

  /** Cancels a pending coupon overlay timer without hiding an active overlay. */
  private clearCouponLoadingDelayTimer(): void {
    if (this.couponLoadingDelayTimer !== null) {
      clearTimeout(this.couponLoadingDelayTimer);
      this.couponLoadingDelayTimer = null;
    }
  }
}
